import { useCallback, useEffect, useRef, useState } from "react";
import { Onboarding } from "./components/Onboarding";
import { ProfileProvider } from "./contexts/ProfileContext";
import { ProfileSwitcher } from "./components/ProfileSwitcher";

interface RoleClaims {
  internal: string | null;
  external: string[];
}

interface AuthUser {
  email: string;
  roles: RoleClaims;
}

type AuthView = "signin" | "signup" | "reset" | "reset-password";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

interface OnboardingStatus {
  status: "not_started" | "in_progress" | "completed";
  currentStep: number;
  completedSteps: string[];
  skippedSteps: string[];
}

interface ProfileData {
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  role_preference: string | null;
  school_name: string | null;
  grade_level: string | null;
  interests: string[] | null;
}

const NETWORK_ERROR_MESSAGE = "Network error. Please check your connection and try again.";
const TOO_MANY_REQUESTS_MESSAGE = "Too many requests. Please wait a moment and try again.";

const isNetworkError = (err: unknown): boolean => {
  return err instanceof TypeError && /failed to fetch/i.test(err.message);
};

const isEmailValid = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const getCsrfFromCookie = (): string | null => {
  const match = /(?:^|;\s*)binaism_csrf=([^;]*)/.exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

export function App(): React.ReactElement {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const csrfTokenRef = useRef<string | null>(null);
  const [authView, setAuthView] = useState<AuthView>("signin");
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [canResendConfirmation, setCanResendConfirmation] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [onboardingProfile, setOnboardingProfile] = useState<ProfileData | null>(null);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [changeNewPassword, setChangeNewPassword] = useState("");
  const [changeConfirmNewPassword, setChangeConfirmNewPassword] = useState("");
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changeNotice, setChangeNotice] = useState<string | null>(null);

  // [fix-csrf-ref] Keep csrfTokenRef in sync with csrfToken state so
  // apiRequest always reads the latest value, even within the same handler.
  useEffect(() => {
    csrfTokenRef.current = csrfToken;
  }, [csrfToken]);

  const apiRequest = useCallback(async (path: string, init?: RequestInit): Promise<Response> => {
    const headers = new Headers(init?.headers);
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const method = (init?.method ?? "GET").toUpperCase();
    const isStateChanging = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
    if (isStateChanging) {
      const token = csrfTokenRef.current ?? getCsrfFromCookie();
      if (token) {
        headers.set("x-csrf-token", token);
      }
    }

    return fetch(`${apiBaseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers,
    });
  }, []);

  const checkOnboardingStatus = useCallback(async (): Promise<void> => {
    try {
      const response = await apiRequest("/api/onboarding/status");
      if (response.ok) {
        const data = (await response.json()) as {
          onboarding: OnboardingStatus;
          profile: ProfileData | null;
        };
        setOnboardingStatus(data.onboarding);
        setOnboardingProfile(data.profile ?? null);
      }
    } catch {
      // Non-fatal — allow the user through if onboarding check fails
    }
    setOnboardingChecked(true);
  }, [apiRequest]);

  useEffect((): (() => void) => {
    let isMounted = true;

    const loadSession = async (): Promise<void> => {
      try {
        const response = await apiRequest("/api/auth/me");
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as { email?: string; roles?: RoleClaims };
        if (isMounted && data.email) {
          setUser({
            email: data.email,
            roles: data.roles ?? { internal: null, external: [] },
          });
          // Restore CSRF token from cookie after page reload
          const cookieCsrf = getCsrfFromCookie();
          if (cookieCsrf) {
            setCsrfToken(cookieCsrf);
          }
          // Check onboarding status for returning users
          await checkOnboardingStatus();
        }
      } catch {
        // Ignore session load errors in UI
      }
    };

    // -----------------------------------------------------------------------
    // Handle query-param callbacks from the backend /auth/callback endpoint.
    // The backend verifies the token_hash server-side, sets cookies, and
    // redirects here with ?auth_action=confirmed|recovery or ?auth_error=...
    // -----------------------------------------------------------------------
    const searchParams = new URLSearchParams(window.location.search);
    const authAction = searchParams.get("auth_action");
    const authError = searchParams.get("auth_error");

    // Clean the URL regardless of which params were present
    if (authAction ?? authError) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    if (authError) {
      if (authError === "expired_recovery") {
        setError("Your password reset link has expired. Please request a new one.");
        setAuthView("reset");
      } else if (authError === "expired_confirmation") {
        setError("Your confirmation link has expired.");
        setCanResendConfirmation(true);
        setAuthView("signin");
      } else {
        setError("Something went wrong. Please try again.");
      }
      return () => {
        isMounted = false;
      };
    }

    if (authAction === "recovery") {
      // Backend already set the session cookie — load user context and show
      // the new-password form.
      const cookieCsrf = getCsrfFromCookie();
      if (cookieCsrf) {
        setCsrfToken(cookieCsrf);
      }
      setAuthView("reset-password");
      return () => {
        isMounted = false;
      };
    }

    if (authAction === "confirmed") {
      // Backend already set the session cookie — load user context and check
      // onboarding.  The loadSession helper handles the isMounted guard.
      void loadSession().then(() => {
        if (isMounted) {
          setNotice("Email confirmed! You are now signed in.");
        }
      });

      // Also pick up the CSRF cookie that the backend callback just set.
      const cookieCsrf = getCsrfFromCookie();
      if (cookieCsrf) {
        setCsrfToken(cookieCsrf);
      }
      return () => {
        isMounted = false;
      };
    }

    // -----------------------------------------------------------------------
    // [hash-fallback] Temporary: handle old-style hash fragments from emails
    // that were sent before the template update.  Remove after 24 h.
    // -----------------------------------------------------------------------
    const hash = window.location.hash.replace(/^#/, "");
    if (hash) {
      const params = new URLSearchParams(hash);
      const hashType = params.get("type");
      const accessToken = params.get("access_token");
      const hashRefreshToken = params.get("refresh_token");

      if (hashType === "recovery" && accessToken) {
        void apiRequest("/api/auth/session", {
          method: "POST",
          body: JSON.stringify({
            accessToken,
            refreshToken: hashRefreshToken ?? undefined,
          }),
        })
          .then(async (response) => {
            if (response.ok) {
              const data = (await response.json()) as { csrfToken?: string };
              if (data.csrfToken && isMounted) {
                setCsrfToken(data.csrfToken);
              }
              if (isMounted) {
                setAuthView("reset-password");
              }
            } else if (isMounted) {
              setError("Your password reset link has expired. Please request a new one.");
              setAuthView("reset");
            }
          })
          .catch((err: unknown) => {
            if (isMounted) {
              if (isNetworkError(err)) {
                setError(NETWORK_ERROR_MESSAGE);
              } else {
                setError("Your password reset link has expired. Please request a new one.");
                setAuthView("reset");
              }
            }
          });
      } else if (hashType === "signup" && accessToken) {
        void apiRequest("/api/auth/session", {
          method: "POST",
          body: JSON.stringify({
            accessToken,
            refreshToken: hashRefreshToken ?? undefined,
          }),
        })
          .then(async (response) => {
            if (response.ok) {
              const data = (await response.json()) as { csrfToken?: string };
              if (data.csrfToken && isMounted) {
                setCsrfToken(data.csrfToken);
              }
              const meResponse = await apiRequest("/api/auth/me");
              if (meResponse.ok) {
                const meData = (await meResponse.json()) as {
                  email?: string;
                  roles?: RoleClaims;
                };
                if (isMounted && meData.email) {
                  setUser({
                    email: meData.email,
                    roles: meData.roles ?? { internal: null, external: [] },
                  });
                  await checkOnboardingStatus();
                  setNotice("Email confirmed! You are now signed in.");
                }
              }
            } else if (isMounted) {
              setError("Your confirmation link has expired.");
              setCanResendConfirmation(true);
              setAuthView("signin");
            }
          })
          .catch((err: unknown) => {
            if (isMounted) {
              if (isNetworkError(err)) {
                setError(NETWORK_ERROR_MESSAGE);
              } else {
                setError("Your confirmation link has expired.");
                setCanResendConfirmation(true);
                setAuthView("signin");
              }
            }
          });
      }

      window.history.replaceState(null, "", window.location.pathname);
    }

    void loadSession();
    return () => {
      isMounted = false;
    };
  }, []);

  const passwordRuleMessage =
    "Password must be at least 6 characters and include upper, lower, and number/symbol.";

  const isPasswordValid = (value: string): boolean => {
    if (value.length < 6) {
      return false;
    }
    const hasUpper = /[A-Z]/.test(value);
    const hasLower = /[a-z]/.test(value);
    const hasNumberOrSymbol = /[0-9\W]/.test(value);
    return hasUpper && hasLower && hasNumberOrSymbol;
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);
    setCanResendConfirmation(false);

    try {
      const response = await apiRequest("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 429) {
        setError(TOO_MANY_REQUESTS_MESSAGE);
        setLoading(false);
        return;
      }

      const data = (await response.json()) as {
        status?: string;
        csrfToken?: string;
        email?: string;
        roles?: RoleClaims;
        error?: string;
        code?: string;
      };

      if (!response.ok) {
        if (data.code === "email_not_confirmed") {
          setNotice("Your email address has not been confirmed yet.");
          setCanResendConfirmation(true);
          setLoading(false);
          return;
        }

        if (data.code === "rate_limited") {
          setError(
            "Your account has been temporarily locked due to too many failed attempts. Please reset your password to regain access."
          );
          setLoading(false);
          return;
        }

        setError(data.error ?? "Invalid email or password");
        setLoading(false);
        return;
      }

      if (data.csrfToken) {
        setCsrfToken(data.csrfToken);
      }
      if (data.email) {
        setUser({
          email: data.email,
          roles: data.roles ?? { internal: null, external: [] },
        });
        await checkOnboardingStatus();
      }

      setPassword("");
      setLoading(false);
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setError(NETWORK_ERROR_MESSAGE);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleSignup = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);
    setCanResendConfirmation(false);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (!isPasswordValid(password)) {
      setError(passwordRuleMessage);
      setLoading(false);
      return;
    }

    try {
      const response = await apiRequest("/api/auth/signup", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 429) {
        setError(TOO_MANY_REQUESTS_MESSAGE);
        setLoading(false);
        return;
      }

      const data = (await response.json()) as {
        status?: string;
        csrfToken?: string;
        email?: string;
        roles?: RoleClaims;
        error?: string;
        code?: string;
      };

      if (!response.ok) {
        if (data.code === "already_registered") {
          setNotice(
            "An account with this email already exists. Please sign in or reset your password."
          );
          setAuthView("signin");
          setPassword("");
          setConfirmPassword("");
        } else {
          setError(data.error ?? "Signup failed");
        }
        setLoading(false);
        return;
      }

      // Email confirmation required — no session yet
      if (data.status === "confirmation_required") {
        setNotice('Check your email for the "Confirm sign up" message to activate your account.');
        setCanResendConfirmation(true);
        setLoading(false);
        return;
      }

      // Immediate session (autoconfirm)
      if (data.csrfToken) {
        setCsrfToken(data.csrfToken);
      }
      if (data.email) {
        setUser({
          email: data.email,
          roles: data.roles ?? { internal: null, external: [] },
        });
        await checkOnboardingStatus();
      }

      setPassword("");
      setConfirmPassword("");
      setLoading(false);
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setError(NETWORK_ERROR_MESSAGE);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleResendConfirmation = async (): Promise<void> => {
    if (!email) {
      setError("Enter your email to resend the confirmation email.");
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      const response = await apiRequest("/api/auth/resend-confirmation", {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setNotice('Confirmation email resent. Check your inbox for "Confirm sign up".');
        setCanResendConfirmation(false);
      } else {
        const data = (await response
          .json()
          .catch(() => ({ error: "Failed to resend confirmation" }))) as { error?: string };
        setError(data.error ?? "Failed to resend confirmation");
      }
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setError(NETWORK_ERROR_MESSAGE);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
    setLoading(false);
  };

  const handlePasswordResetRequest = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      const response = await apiRequest("/api/auth/reset-request", {
        method: "POST",
        body: JSON.stringify({ email: resetEmail }),
      });

      if (response.ok) {
        setNotice('Check your email for the "Reset password" message to continue.');
      } else {
        const data = (await response
          .json()
          .catch(() => ({ error: "Failed to send password reset email" }))) as {
          error?: string;
        };
        setError(data.error ?? "Failed to send password reset email");
      }
      setLoading(false);
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setError(NETWORK_ERROR_MESSAGE);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handlePasswordReset = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);

    // [password-confirm] Validate confirm password matches
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError(passwordRuleMessage);
      setLoading(false);
      return;
    }

    try {
      const response = await apiRequest("/api/auth/password", {
        method: "POST",
        body: JSON.stringify({ password: newPassword }),
      });

      // [handle-429] Detect HTTP 429 from backend
      if (response.status === 429) {
        setError(TOO_MANY_REQUESTS_MESSAGE);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const data = (await response
          .json()
          .catch(() => ({ error: "Unable to reset password" }))) as {
          error?: string;
        };
        setError(data.error ?? "Unable to reset password");
        setLoading(false);
        return;
      }

      setNotice("Password updated. You can now sign in.");
      setAuthView("signin");
      setNewPassword("");
      setConfirmNewPassword("");
      setLoading(false);
    } catch (err: unknown) {
      // [network-errors] Friendly network error message
      if (isNetworkError(err)) {
        setError(NETWORK_ERROR_MESSAGE);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setChangeError(null);
    setChangeNotice(null);

    if (changeNewPassword === currentPassword) {
      setChangeError("New password cannot be the same as your current password.");
      setLoading(false);
      return;
    }

    if (changeNewPassword !== changeConfirmNewPassword) {
      setChangeError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (!isPasswordValid(changeNewPassword)) {
      setChangeError(passwordRuleMessage);
      setLoading(false);
      return;
    }

    try {
      // Send currentPassword to backend — it verifies server-side via GoTrue
      const response = await apiRequest("/api/auth/password", {
        method: "POST",
        body: JSON.stringify({
          password: changeNewPassword,
          currentPassword,
        }),
      });

      if (response.status === 429) {
        setChangeError(TOO_MANY_REQUESTS_MESSAGE);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const data = (await response
          .json()
          .catch(() => ({ error: "Unable to change password" }))) as {
          error?: string;
        };
        setChangeError(data.error ?? "Unable to change password");
        setLoading(false);
        return;
      }

      setChangeNotice("Password changed successfully.");
      setCurrentPassword("");
      setChangeNewPassword("");
      setChangeConfirmNewPassword("");
      setLoading(false);
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setChangeError(NETWORK_ERROR_MESSAGE);
      } else {
        setChangeError("An unexpected error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await apiRequest("/api/auth/logout", { method: "POST" });
    } catch {
      // Sign out locally even if the API call fails
    }
    setUser(null);
    setCsrfToken(null);
    setOnboardingStatus(null);
    setOnboardingProfile(null);
    setOnboardingChecked(false);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900">Binaism</h1>

        {user && onboardingChecked && onboardingStatus?.status !== "completed" ? (
          <Onboarding
            apiRequest={apiRequest}
            onSignOut={() => {
              void handleLogout();
            }}
            onComplete={() => {
              setOnboardingStatus({
                status: "completed",
                currentStep: 3,
                completedSteps: [],
                skippedSteps: [],
              });
              // Trigger a server-side token refresh so the new JWT includes the
              // role assigned during onboarding.  The backend owns the refresh
              // token — no client-side Supabase SDK calls needed.
              void (async () => {
                try {
                  const refreshRes = await apiRequest("/api/auth/refresh", {
                    method: "POST",
                  });
                  if (refreshRes.ok) {
                    const refreshJson = (await refreshRes.json()) as {
                      csrfToken?: string;
                      email?: string;
                      roles?: RoleClaims;
                    };
                    if (refreshJson.csrfToken) {
                      setCsrfToken(refreshJson.csrfToken);
                    }
                    if (refreshJson.email) {
                      setUser({
                        email: refreshJson.email,
                        roles: refreshJson.roles ?? { internal: null, external: [] },
                      });
                    }
                  }
                } catch {
                  // Non-fatal — roles will update on next login
                }
              })();
            }}
            initialStatus={onboardingStatus}
            initialProfile={onboardingProfile}
          />
        ) : user ? (
          <ProfileProvider apiRequest={apiRequest}>
            <div className="mt-6 max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <p className="text-gray-700">
                  Signed in as <span className="font-semibold">{user.email}</span>
                </p>
                <ProfileSwitcher />
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(!showChangePassword);
                    setChangeError(null);
                    setChangeNotice(null);
                    setCurrentPassword("");
                    setChangeNewPassword("");
                    setChangeConfirmNewPassword("");
                  }}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Change password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleLogout();
                  }}
                  className="inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                  disabled={loading}
                >
                  Sign out
                </button>
              </div>

              {showChangePassword ? (
                <form
                  className="mt-6 border-t border-gray-200 pt-6"
                  onSubmit={(event) => {
                    void handleChangePassword(event);
                  }}
                >
                  <h2 className="text-lg font-semibold text-gray-900">Change password</h2>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Current password
                    </label>
                    <input
                      type="password"
                      required
                      value={currentPassword}
                      onChange={(event) => {
                        setCurrentPassword(event.target.value);
                      }}
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">New password</label>
                    <input
                      type="password"
                      required
                      value={changeNewPassword}
                      onChange={(event) => {
                        setChangeNewPassword(event.target.value);
                      }}
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <p className="mt-2 text-xs text-gray-500">{passwordRuleMessage}</p>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      required
                      value={changeConfirmNewPassword}
                      onChange={(event) => {
                        setChangeConfirmNewPassword(event.target.value);
                      }}
                      className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    {changeConfirmNewPassword && changeConfirmNewPassword !== changeNewPassword ? (
                      <p className="mt-1 text-xs text-red-500" role="alert">
                        Passwords do not match.
                      </p>
                    ) : null}
                  </div>
                  <div aria-live="assertive" role="alert">
                    {changeError ? (
                      <p className="mt-4 text-sm text-red-600">{changeError}</p>
                    ) : null}
                  </div>
                  <div aria-live="polite" role="status">
                    {changeNotice ? (
                      <p className="mt-4 text-sm text-green-600">{changeNotice}</p>
                    ) : null}
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                    >
                      {loading ? "Updating..." : "Update password"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowChangePassword(false);
                        setChangeError(null);
                        setChangeNotice(null);
                      }}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : null}
            </div>
          </ProfileProvider>
        ) : (
          <div className="mt-6 max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            {authView === "signin" ? (
              <form
                onSubmit={(event) => {
                  void handleLogin(event);
                }}
              >
                <h2 className="text-lg font-semibold text-gray-900">Sign in</h2>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {/* [inline-email-validation] */}
                  {email && !isEmailValid(email) ? (
                    <p className="mt-1 text-xs text-red-500" role="alert">
                      Please enter a valid email address.
                    </p>
                  ) : null}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div aria-live="assertive" role="alert">
                  {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
                </div>
                <div aria-live="polite" role="status">
                  {notice ? <p className="mt-4 text-sm text-green-600">{notice}</p> : null}
                </div>
                {/* [account-lockout] Show reset button when account is locked */}
                {error?.includes("temporarily locked") ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAuthView("reset");
                      setError(null);
                      setNotice(null);
                    }}
                    className="mt-2 text-sm text-gray-700 underline"
                  >
                    Reset your password
                  </button>
                ) : null}
                {/* [unconfirmed-signin] Show resend button on sign-in when email not confirmed */}
                {canResendConfirmation ? (
                  <button
                    type="button"
                    onClick={() => {
                      void handleResendConfirmation();
                    }}
                    className="mt-3 text-sm text-gray-700 underline"
                    disabled={loading}
                  >
                    Resend confirmation email
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
                <div className="mt-4 space-y-2 text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthView("signup");
                      setError(null);
                      setNotice(null);
                      setCanResendConfirmation(false);
                    }}
                    className="block text-left text-gray-700 underline"
                  >
                    Create an Account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthView("reset");
                      setError(null);
                      setNotice(null);
                      setCanResendConfirmation(false);
                    }}
                    className="block text-left text-gray-700 underline"
                  >
                    Having Trouble with Your Username/Password?
                  </button>
                </div>
              </form>
            ) : null}

            {authView === "signup" ? (
              <form
                onSubmit={(event) => {
                  void handleSignup(event);
                }}
              >
                <h2 className="text-lg font-semibold text-gray-900">Create an account</h2>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {/* [inline-email-validation] */}
                  {email && !isEmailValid(email) ? (
                    <p className="mt-1 text-xs text-red-500" role="alert">
                      Please enter a valid email address.
                    </p>
                  ) : null}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <p className="mt-2 text-xs text-gray-500">{passwordRuleMessage}</p>
                </div>
                {/* [password-confirm] Confirm password field on signup */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {confirmPassword && confirmPassword !== password ? (
                    <p className="mt-1 text-xs text-red-500" role="alert">
                      Passwords do not match.
                    </p>
                  ) : null}
                </div>
                <div aria-live="assertive" role="alert">
                  {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
                </div>
                <div aria-live="polite" role="status">
                  {notice ? <p className="mt-4 text-sm text-green-600">{notice}</p> : null}
                </div>
                {canResendConfirmation ? (
                  <button
                    type="button"
                    onClick={() => {
                      void handleResendConfirmation();
                    }}
                    className="mt-3 text-sm text-gray-700 underline"
                    disabled={loading}
                  >
                    Resend confirmation email
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  {loading ? "Creating account..." : "Create account"}
                </button>
                <div className="mt-4 text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthView("signin");
                      setError(null);
                      setNotice(null);
                      setCanResendConfirmation(false);
                    }}
                    className="text-gray-700 underline"
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            ) : null}

            {authView === "reset" ? (
              <form
                onSubmit={(event) => {
                  void handlePasswordResetRequest(event);
                }}
              >
                <h2 className="text-lg font-semibold text-gray-900">Reset password</h2>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(event) => {
                      setResetEmail(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {/* [inline-email-validation] */}
                  {resetEmail && !isEmailValid(resetEmail) ? (
                    <p className="mt-1 text-xs text-red-500" role="alert">
                      Please enter a valid email address.
                    </p>
                  ) : null}
                </div>
                <div aria-live="assertive" role="alert">
                  {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
                </div>
                <div aria-live="polite" role="status">
                  {notice ? <p className="mt-4 text-sm text-green-600">{notice}</p> : null}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  {loading ? "Sending reset..." : "Send reset email"}
                </button>
                <div className="mt-4 text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthView("signin");
                      setError(null);
                      setNotice(null);
                      setCanResendConfirmation(false);
                    }}
                    className="text-gray-700 underline"
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            ) : null}

            {authView === "reset-password" ? (
              <form
                onSubmit={(event) => {
                  void handlePasswordReset(event);
                }}
              >
                <h2 className="text-lg font-semibold text-gray-900">Set new password</h2>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">New password</label>
                  <input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(event) => {
                      setNewPassword(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <p className="mt-2 text-xs text-gray-500">{passwordRuleMessage}</p>
                </div>
                {/* [password-confirm] Confirm password field on reset-password */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Confirm new password
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmNewPassword}
                    onChange={(event) => {
                      setConfirmNewPassword(event.target.value);
                    }}
                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {confirmNewPassword && confirmNewPassword !== newPassword ? (
                    <p className="mt-1 text-xs text-red-500" role="alert">
                      Passwords do not match.
                    </p>
                  ) : null}
                </div>
                <div aria-live="assertive" role="alert">
                  {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
                </div>
                <div aria-live="polite" role="status">
                  {notice ? <p className="mt-4 text-sm text-green-600">{notice}</p> : null}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  {loading ? "Updating password..." : "Update password"}
                </button>
              </form>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
